import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { CommunityFeedItem } from '@shared/types';

import { InfiniteSentinel } from '@/components/InfiniteSentinel';
import { NotificationsBell } from '@/components/NavRail';
import { Reveal, RevealGroup } from '@/components/Reveal';
import { RowSkeleton } from '@/components/RowSkeleton';
import { ErrorState, StateBlock } from '@/components/StateBlock';
import { Button } from '@/components/ui/button';
import {
  flattenActivityPages,
  flattenFollowingPages,
  flattenTrendingPages,
  useFollowingFeed,
  useFriendsActivity,
  useTrendingFeed,
  useWatchingFriends,
  type FriendActivityItem,
} from '@/features/friends/api';
import { useLibraryStatusMap } from '@/features/library/api';
import { ProfileSetupPrompt } from '@/features/onboarding/ProfileSetupPrompt';
import { useMyProfile } from '@/features/profile/api';
import { useUpNext } from '@/features/upnext/api';
import { useLocale } from '@/i18n/locale';
import { pageVariants, prefersReducedMotion, staggerChild } from '@/lib/motion';

import { useBecauseYouFinished, useFeedHeads, useSessionCutoff } from './api';
import { FeedCardView, OwnedTitlesProvider } from './FeedCards';
import { buildFeed, type FeedCard, type FeedSources } from './feed-model';
import { homeGreeting } from './greeting';
import { AlsoInProgress, NowShowing, NowShowingSkeleton } from './NowShowing';
import { AiringThisWeek, HotOnShelf, WatchingNow } from './Sidebar';

/**
 * `/` — Tonight, the home (docs/screens.md §`/`), "projection room, lights
 * down": a one-line greeting, then the screen is the hero — the first Up next
 * title as a large perspective case beside its copy (its Continue is the
 * view's one amber action) with the rest of Up next as a rail of cases — all
 * one load settle (`RevealGroup id="tonight"`) — then the friends
 * timeline (one chronological stream merged from friends' activity +
 * comments with capped trending / "because you finished" inserts,
 * `feed-model.ts`) beside a sticky column of the hour-by-hour things (Airing
 * this week, Friends watching now, Hot on your shelf) at `lg`.
 */

/** The hero + up to nine covers on the "Also in progress" rail. */
const UP_NEXT_LIMIT = 10;
/** "First screen" of the stream — the sidebar's hot card defers to it. */
const FIRST_SCREEN_CARDS = 5;

function dedupeById<T>(items: T[], id: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = id(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function cardTime(card: FeedCard): number | null {
  if (card.kind === 'hot' || card.kind === 'because') return null;
  const t = Date.parse(card.at);
  return Number.isNaN(t) ? null : t;
}

function showsComment(card: FeedCard, commentId: string): boolean {
  return (card.kind === 'hot' || card.kind === 'commented') && card.item.comment.commentId === commentId;
}

export function TonightScreen() {
  const { locale, t } = useLocale();
  const profile = useMyProfile();

  const activity = useFriendsActivity();
  const following = useFollowingFeed();
  const trending = useTrendingFeed();
  const watching = useWatchingFriends(8);
  const because = useBecauseYouFinished();
  const owned = useLibraryStatusMap();
  const heads = useFeedHeads(!activity.isPending, !following.isPending);
  const [cutoff, setCutoff] = useSessionCutoff();

  const activityItems = useMemo(
    () =>
      dedupeById<FriendActivityItem>(
        [...heads.activity, ...flattenActivityPages(activity.data)],
        (item) => item.id,
      ),
    [heads.activity, activity.data],
  );
  const commentItems = useMemo(
    () =>
      dedupeById<CommunityFeedItem>(
        [...heads.following, ...flattenFollowingPages(following.data)],
        (item) => item.comment.commentId,
      ),
    [heads.following, following.data],
  );
  const trendingItems = useMemo(() => flattenTrendingPages(trending.data), [trending.data]);

  const { model, sidebarHot } = useMemo(() => {
    const now = Date.now();
    const sources: FeedSources = {
      activity: activityItems,
      comments: commentItems,
      hot: trendingItems,
      because,
      activityHasMore: activity.hasNextPage,
      commentsHasMore: following.hasNextPage,
      holdNewerThan: cutoff,
    };
    const first = buildFeed(sources, now);
    const top = trendingItems.find((item) => item.comment.body !== null);
    if (
      !top ||
      first.cards.slice(0, FIRST_SCREEN_CARDS).some((card) => showsComment(card, top.comment.commentId))
    ) {
      return { model: first, sidebarHot: null };
    }
    // The sidebar shows the top trending comment; the stream skips it.
    const model = buildFeed({ ...sources, excludeHotIds: new Set([top.comment.commentId]) }, now);
    return { model, sidebarHot: top };
  }, [
    activityItems,
    commentItems,
    trendingItems,
    because,
    activity.hasNextPage,
    following.hasNextPage,
    cutoff,
  ]);

  // First load of the session: everything on screen is "seen".
  const friendsSettled = !activity.isPending && !following.isPending;
  useEffect(() => {
    if (friendsSettled && cutoff === null) setCutoff(model.newest ?? Date.now());
  }, [friendsSettled, cutoff, model.newest, setCutoff]);

  const [animateNewerThan, setAnimateNewerThan] = useState<number | null>(null);
  const streamHeadingRef = useRef<HTMLHeadingElement>(null);
  const reveal = () => {
    if (model.newest === null) return;
    setAnimateNewerThan(cutoff);
    setCutoff(model.newest);
    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    streamHeadingRef.current?.focus({ preventScroll: true });
  };

  const friendCards = model.cards.filter((c) => c.kind !== 'hot' && c.kind !== 'because').length;
  // A source that failed its FIRST page (a later page's failure is the
  // sentinel's footer). With nothing else to show it is the section's error;
  // next to other friend cards it is a one-line notice.
  const activityFailed = activity.isError && !activity.isFetchNextPageError;
  const followingFailed = following.isError && !following.isFetchNextPageError;
  const anyFailed = activityFailed || followingFailed;
  const bothFailed = anyFailed && friendCards === 0 && model.held === 0;
  const partialFailure = anyFailed && !bothFailed;
  const noFriendItems = friendsSettled && !anyFailed && friendCards === 0 && model.held === 0;
  const followsNobody = noFriendItems && !watching.isPending && (watching.data ?? []).length === 0;

  const loadMore = () => {
    if (activity.hasNextPage && !activity.isFetchingNextPage) void activity.fetchNextPage();
    if (following.hasNextPage && !following.isFetchingNextPage) void following.fetchNextPage();
  };

  return (
    <RevealGroup id="tonight">
      <section className="flex flex-col gap-8 sm:gap-12">
        <Reveal as="header" className="-mb-2 flex items-center justify-between gap-4 pt-2 sm:-mb-4">
          <h1 className="text-section text-ink sm:text-display-sm">
            {homeGreeting(new Date(), locale, profile.data?.displayName)}
          </h1>
          <NotificationsBell className="md:hidden" />
        </Reveal>
        <ProfileSetupPrompt />

        <UpNextSection />

        {/* One max-width container, no dead gutter: the timeline takes the
            room and the hour-by-hour column sits 48px beside it at `lg`.
            The column comes first in the DOM so below `lg` it reads (and
            shows) before the timeline. */}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
          <aside
            aria-label={t('tonight.sidebar')}
            className="flex min-w-0 flex-col gap-8 lg:sticky lg:top-8 lg:col-start-2 lg:row-start-1 lg:self-start lg:gap-10"
          >
            <AiringThisWeek />
            <WatchingNow />
            {sidebarHot ? <HotOnShelf item={sidebarHot} /> : null}
          </aside>

          <section
            aria-labelledby="tonight-stream"
            className="relative flex min-w-0 flex-col gap-3 lg:col-start-1 lg:row-start-1"
          >
            {/* "N new" — a zero-height sticky anchor at the top of the timeline
                column: at rest the pill sits at the right end of the heading
                row; scrolled, it stays pinned at the column's top-right. It
                never takes layout space (`-mb-3` cancels the column gap), so
                nothing shifts. */}
            <div className="pointer-events-none sticky top-4 z-10 -mb-3 h-0">
              <div className="absolute right-0 top-0">
                <AnimatePresence>
                  {model.held > 0 ? (
                    <motion.div
                      key="new-pill"
                      variants={pageVariants()}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="pointer-events-auto"
                    >
                      <Button
                        variant="neutral"
                        size="sm"
                        onClick={reveal}
                        className="rounded-full bg-surface shadow-sm"
                      >
                        <ArrowUp className="size-4" strokeWidth={1.75} aria-hidden />
                        {t('tonight.newItems', { count: model.held })}
                      </Button>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
            {/* min-h-9 = the pill's height, so the pill and the heading share
                one row — and the right column's first heading row matches it. */}
            <div className="flex min-h-9 items-center pr-32">
              <h2
                id="tonight-stream"
                ref={streamHeadingRef}
                tabIndex={-1}
                className="text-section text-ink"
              >
                {t('tonight.stream')}
              </h2>
            </div>
            <p className="sr-only" role="status">
              {model.held > 0 ? t('tonight.newItemsA11y', { count: model.held }) : ''}
            </p>

            {!friendsSettled ? (
              <RowSkeleton count={4} variant="avatar" label={t('tonight.loadingFeed')} />
            ) : bothFailed ? (
              <ErrorState
                size="inline"
                title={t('tonight.error.feed')}
                onRetry={() => {
                  if (activityFailed) void activity.refetch();
                  if (followingFailed) void following.refetch();
                }}
              />
            ) : (
              <>
                {partialFailure ? (
                  <div role="alert" className="flex flex-wrap items-center gap-2 text-caption text-ink-secondary">
                    <span>{t('tonight.error.partial')}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (activityFailed) void activity.refetch();
                        if (followingFailed) void following.refetch();
                      }}
                    >
                      {t('common.retry')}
                    </Button>
                  </div>
                ) : null}
                {noFriendItems ? (
                  <StateBlock
                    role="status"
                    size="inline"
                    title={followsNobody ? t('tonight.empty.noFriends') : t('tonight.empty.quiet')}
                    body={followsNobody ? t('tonight.empty.noFriendsBody') : t('tonight.empty.quietBody')}
                    action={
                      <Button asChild variant="neutral">
                        <Link to="/people-search">
                          <UserPlus className="size-4" strokeWidth={1.75} aria-hidden />
                          {t('tonight.findPeople')}
                        </Link>
                      </Button>
                    }
                  />
                ) : null}
                {model.cards.length > 0 ? (
                  <OwnedTitlesProvider owned={owned}>
                    <ul className="m-0 flex list-none flex-col gap-1 p-0">
                      {model.cards.map((card) => {
                        const at = cardTime(card);
                        const animate = animateNewerThan !== null && at !== null && at > animateNewerThan;
                        return animate ? (
                          <motion.li key={card.key} variants={staggerChild()} initial="initial" animate="animate">
                            <FeedCardView card={card} />
                          </motion.li>
                        ) : (
                          <li key={card.key}>
                            <FeedCardView card={card} />
                          </li>
                        );
                      })}
                    </ul>
                  </OwnedTitlesProvider>
                ) : null}
                <InfiniteSentinel
                  onReach={loadMore}
                  hasMore={Boolean(activity.hasNextPage || following.hasNextPage)}
                  loading={activity.isFetchingNextPage || following.isFetchingNextPage}
                  error={activity.isFetchNextPageError || following.isFetchNextPageError}
                  onRetry={loadMore}
                />
              </>
            )}
          </section>
        </div>
      </section>
    </RevealGroup>
  );
}

/**
 * Up next: the first title as the "now showing" hero (the view's one amber
 * action), the rest as the "Also in progress" rail of cases.
 */
function UpNextSection() {
  const { t } = useLocale();
  const upNext = useUpNext(UP_NEXT_LIMIT);
  const [first, ...rest] = upNext.items;

  let body;
  if (upNext.isPending) {
    body = <NowShowingSkeleton />;
  } else if (upNext.isError) {
    body = <ErrorState size="inline" title={t('tonight.error.upNext')} onRetry={upNext.refetch} />;
  } else if (!first) {
    body = (
      <div className="flex min-h-[clamp(220px,32vh,360px)] items-center rounded-lg bg-surface px-6 sm:px-10">
        <StateBlock
          role="status"
          size="inline"
          title={t('tonight.upNextEmpty')}
          body={t('tonight.upNextEmptyBody')}
          action={
            <Button asChild variant="accent">
              <Link to="/discover">{t('tonight.exploreDiscover')}</Link>
            </Button>
          }
        />
      </div>
    );
  } else {
    // Its own group: when Up next lands after the greeting, the hero's beats
    // run from that moment; the rail's cases follow the hero (order 9).
    body = (
      <RevealGroup id="tonight-up-next">
        <NowShowing item={first} />
        {rest.length > 0 ? (
          <RevealGroup id="tonight-also" order={9}>
            <AlsoInProgress items={rest} />
          </RevealGroup>
        ) : null}
      </RevealGroup>
    );
  }

  return (
    <section aria-label={t('tonight.upNext')} className="flex flex-col gap-10 sm:gap-12">
      {body}
    </section>
  );
}
